import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../Store/store";
import { setUserTier } from "../Redux/authReducer";
import { useGetMeQuery } from "../Services/api.user.slice";

export type PlanSource = "paid" | "escrow" | "gift" | "launch" | null;

export interface Plan {
  tier: "free" | "premium";
  source: PlanSource;
  /** Renewal date (paid) or grant end; null = no end. ISO string. */
  until: string | null;
  cancelAtPeriodEnd: boolean;
  /** Raw Stripe subscription status — drives the past_due "update card" banner. */
  status: string | null;
}

/**
 * The signed-in user's current plan from GET /api/me — the authoritative,
 * post-checkout view (source, renewal/end date, cancel-at-period-end, Stripe
 * status), as opposed to useEntitlements' display-only tier from the persisted
 * session. Whenever /api/me's tier differs from the cached session tier it
 * syncs it (setUserTier), so the UI gates unlock/relock in place without a
 * re-login. Skips the request while signed out.
 */
export const usePlan = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s: RootState) => s?.auth?.isAuthenticated);
  const sessionTier = useSelector((s: RootState) => (s?.auth?.user as any)?.tier);

  const { data, isLoading, isFetching, refetch } = useGetMeQuery(undefined, {
    skip: !isAuthenticated,
  });
  const plan: Plan | undefined = data?.plan;

  useEffect(() => {
    const t = plan?.tier;
    if (t && t !== sessionTier) dispatch(setUserTier(t));
  }, [plan?.tier, sessionTier, dispatch]);

  return {
    isAuthenticated: Boolean(isAuthenticated),
    plan,
    user: data?.user,
    isLoading,
    isFetching,
    refetch,
  };
};

/** "Nov 30, 2027" from an ISO string, or null if it isn't a date. */
export const formatPlanDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};
